import { useEffect, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  listChinaMarketsApi,
  updateChinaMarketStatusApi,
  deleteChinaMarketApi,
  MarketItem,
  ModerationStatus,
  RelevanceStatus,
  VerificationType,
} from "../../features/china-markets/api/china-markets.api"
import { MarketCategoriesModal } from "./MarketCategoriesModal"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import {
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  ClockIcon,
  MapPinIcon,
  TagIcon,
  StarIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon, PencilSquareIcon } from "@heroicons/react/20/solid"

const LIMIT = 15

/* ── Moderation config ── */
const MODERATION_BADGE: Record<ModerationStatus, { className: string; dot: string; label: string; gradient: string }> = {
  on_moderation: {
    className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    dot: "bg-amber-400",
    label: "На модерации",
    gradient: "from-amber-500 to-yellow-400",
  },
  approved: {
    className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    dot: "bg-emerald-500",
    label: "Одобрено",
    gradient: "from-emerald-500 to-green-400",
  },
  rejected: {
    className: "bg-red-50 text-red-700 ring-1 ring-red-200",
    dot: "bg-red-500",
    label: "Отклонено",
    gradient: "from-red-500 to-rose-400",
  },
}

const RELEVANCE_BADGE: Record<RelevanceStatus, { className: string; label: string }> = {
  active:       { className: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",     label: "Актуально" },
  needs_update: { className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",  label: "Требует обновления" },
  outdated:     { className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",    label: "Устарело" },
}

const VERIFICATION_BADGE: Record<VerificationType, { className: string; label: string }> = {
  none:   { className: "bg-gray-100 text-gray-500",     label: "Нет" },
  system: { className: "bg-violet-50 text-violet-700",  label: "Система" },
  buyer:  { className: "bg-sky-50 text-sky-700",        label: "Покупатель" },
  admin:  { className: "bg-indigo-50 text-indigo-700",  label: "Администратор" },
}

const ModerationBadge = ({ status }: { status: ModerationStatus }) => {
  const s = MODERATION_BADGE[status] ?? { className: "bg-gray-100 text-gray-500", dot: "bg-gray-400", label: status }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {s.label}
    </span>
  )
}

const RelevanceBadge = ({ status }: { status: RelevanceStatus }) => {
  const s = RELEVANCE_BADGE[status] ?? { className: "bg-gray-100 text-gray-500", label: status }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      {s.label}
    </span>
  )
}

const VerificationBadge = ({ type }: { type: VerificationType }) => {
  const s = VERIFICATION_BADGE[type] ?? { className: "bg-gray-100 text-gray-500", label: type }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      {s.label}
    </span>
  )
}

export const ChinaMarketsPage = () => {
  const [items, setItems] = useState<MarketItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const [pendingSearch, setPendingSearch] = useState("")
  const [search, setSearch] = useState("")
  const [moderationFilter, setModerationFilter] = useState<ModerationStatus | "">("")
  const [categoriesOpen, setCategoriesOpen] = useState(false)

  /* Drawer */
  const [selected, setSelected] = useState<MarketItem | null>(null)
  const [editModeration, setEditModeration] = useState<ModerationStatus>("on_moderation")
  const [editVerification, setEditVerification] = useState<VerificationType>("none")
  const [editRelevance, setEditRelevance] = useState<RelevanceStatus>("active")
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  /* ── Load ── */
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (page - 1) * LIMIT
      const res = await listChinaMarketsApi({
        limit: LIMIT,
        offset,
        ...(moderationFilter ? { moderation_status: moderationFilter } : {}),
        ...(search ? { search } : {}),
      })
      setItems(res.data.markets ?? [])
      setTotal(res.data.total ?? res.data.markets?.length ?? 0)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [page, moderationFilter, search])

  useEffect(() => { load() }, [load])

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const handleSearch = () => { setSearch(pendingSearch); setPage(1) }

  const openDrawer = (item: MarketItem) => {
    setSelected(item)
    setEditModeration(item.moderation_status)
    setEditVerification(item.verification_type)
    setEditRelevance(item.relevance_status)
    setSaveOk(false)
    setConfirmDelete(false)
  }

  const handleSave = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await updateChinaMarketStatusApi(selected.id, {
        moderation_status: editModeration,
        verification_type: editVerification,
        relevance_status: editRelevance,
      })
      setItems((prev) =>
        prev.map((i) =>
          i.id === selected.id
            ? { ...i, moderation_status: editModeration, verification_type: editVerification, relevance_status: editRelevance }
            : i
        )
      )
      setSelected((prev) => prev ? { ...prev, moderation_status: editModeration, verification_type: editVerification, relevance_status: editRelevance } : null)
      setSaveOk(true)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    setDeleting(true)
    try {
      await deleteChinaMarketApi(selected.id)
      setItems((prev) => prev.filter((i) => i.id !== selected.id))
      setTotal((t) => Math.max(0, t - 1))
      setSelected(null)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <DashboardLayout title="Рынки Китая">

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex flex-1 min-w-[220px] max-w-sm items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Поиск по названию…"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 text-sm outline-none"
          />
        </div>

        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
        >
          Найти
        </button>

        <FilterDropdown
          label="Статус"
          value={moderationFilter}
          onChange={(v) => { setModerationFilter(v); setPage(1) }}
          options={[
            { value: "on_moderation", label: "На модерации", dot: "bg-amber-400" },
            { value: "approved", label: "Одобрено", dot: "bg-emerald-400" },
            { value: "rejected", label: "Отклонено", dot: "bg-red-400" },
          ]}
        />

        <button
          onClick={() => setCategoriesOpen(true)}
          className="inline-flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm px-3.5 py-2 rounded-lg transition-colors"
        >
          <TagIcon className="w-4 h-4 text-gray-400" />
          Категории
        </button>

        <span className="ml-auto text-xs text-gray-400">
          {total} рынков
        </span>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/40">
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Название</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Категория</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Рейтинг</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Модерация</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Актуальность</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Верификация</th>
              <th className="px-5 py-3 w-20" />
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {[1,2,3,4,5,6,7].map((j) => (
                    <td key={j} className="px-5 py-4">
                      <div className="h-4 bg-gray-100 rounded animate-pulse w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-16 text-center">
                  <BuildingStorefrontIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Рынков не найдено</p>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} onClick={() => openDrawer(item)} className="hover:bg-gray-50/60 transition-colors cursor-pointer group">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                        <BuildingStorefrontIcon className="w-4 h-4 text-red-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate max-w-[200px]">{item.name}</p>
                        {item.name_cn && <p className="text-xs text-gray-400 truncate max-w-[200px]">{item.name_cn}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-gray-500">
                    {item.category?.codename ?? <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-gray-600">
                    {item.average_rating != null ? (
                      <span className="inline-flex items-center gap-1">
                        <StarIcon className="w-3.5 h-3.5 text-amber-400" />
                        {item.average_rating.toFixed(1)}
                        <span className="text-gray-300">({item.reviews_count ?? 0})</span>
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-5 py-3.5"><ModerationBadge status={item.moderation_status} /></td>
                  <td className="px-5 py-3.5"><RelevanceBadge status={item.relevance_status} /></td>
                  <td className="px-5 py-3.5"><VerificationBadge type={item.verification_type} /></td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); openDrawer(item) }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                      Изменить
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Назад
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Categories modal ── */}
      <MarketCategoriesModal open={categoriesOpen} onClose={() => setCategoriesOpen(false)} />

      {/* ── Drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${selected ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setSelected(null)}
          />
          <div
            className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${selected ? "translate-x-0" : "translate-x-full"}`}
          >
            {selected && (
              <>
                {/* ── Header ── */}
                <div className={`bg-gradient-to-r ${MODERATION_BADGE[selected.moderation_status].gradient} px-6 pt-6 pb-8 flex-shrink-0 relative`}>
                  <button
                    onClick={() => setSelected(null)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 text-white" />
                  </button>

                  <div className="flex items-center gap-2 mb-3">
                    <BuildingStorefrontIcon className="w-5 h-5 text-white/80" />
                    <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Рынок</span>
                  </div>

                  {/* Name */}
                  <div className="flex items-center gap-3 pr-10">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <BuildingStorefrontIcon className="w-6 h-6 text-white/70" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-lg leading-tight">{selected.name}</p>
                      {selected.name_cn && <p className="text-white/70 text-sm mt-0.5">{selected.name_cn}{selected.pinyin ? ` · ${selected.pinyin}` : ""}</p>}
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <ModerationBadge status={selected.moderation_status} />
                    <RelevanceBadge status={selected.relevance_status} />
                    <VerificationBadge type={selected.verification_type} />
                  </div>
                </div>

                {/* ── Body ── */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                  {/* Location */}
                  {(selected.address || selected.metro) && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <MapPinIcon className="w-3.5 h-3.5" />
                        Расположение
                      </p>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-1">
                        {selected.address && <p className="text-sm text-gray-700">{selected.address}</p>}
                        {selected.metro && <p className="text-xs text-gray-400">{selected.metro}</p>}
                      </div>
                    </div>
                  )}

                  {/* Category + rating */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Категория</p>
                      <p className="text-sm text-gray-700 font-mono">{selected.category?.codename ?? "—"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Рейтинг</p>
                      <p className="text-sm text-gray-700">
                        {selected.average_rating != null ? `${selected.average_rating.toFixed(1)} (${selected.reviews_count ?? 0})` : "—"}
                      </p>
                    </div>
                  </div>

                  {/* ID */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">ID</p>
                    <p className="font-mono text-xs text-gray-600 break-all">{selected.id}</p>
                  </div>

                  {/* ── Moderation status ── */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <ClockIcon className="w-3.5 h-3.5" />
                      Статус модерации
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {(["on_moderation", "approved", "rejected"] as ModerationStatus[]).map((s) => {
                        const cfg = MODERATION_BADGE[s]
                        const active = editModeration === s
                        return (
                          <button
                            key={s}
                            onClick={() => setEditModeration(s)}
                            className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-medium transition-all ${
                              active
                                ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full ${active ? "bg-white" : cfg.dot}`} />
                            {cfg.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* ── Verification type ── */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <ShieldCheckIcon className="w-3.5 h-3.5" />
                      Верификация
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {(["none", "system", "buyer", "admin"] as VerificationType[]).map((v) => {
                        const cfg = VERIFICATION_BADGE[v]
                        const active = editVerification === v
                        return (
                          <button
                            key={v}
                            onClick={() => setEditVerification(v)}
                            className={`py-2.5 px-2 rounded-xl border text-xs font-medium transition-all ${
                              active
                                ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {cfg.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* ── Relevance status ── */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Актуальность</p>
                    <div className="grid grid-cols-3 gap-2">
                      {(["active", "needs_update", "outdated"] as RelevanceStatus[]).map((r) => {
                        const cfg = RELEVANCE_BADGE[r]
                        const active = editRelevance === r
                        return (
                          <button
                            key={r}
                            onClick={() => setEditRelevance(r)}
                            className={`py-2.5 px-2 rounded-xl border text-xs font-medium transition-all text-center ${
                              active
                                ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {cfg.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Feedback */}
                  {saveOk && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                      Изменения сохранены
                    </div>
                  )}

                  {/* ── Delete ── */}
                  <div className="space-y-2 pt-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Удаление</p>
                    {!confirmDelete ? (
                      <button
                        onClick={() => setConfirmDelete(true)}
                        className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Удалить рынок
                      </button>
                    ) : (
                      <div className="border border-red-200 rounded-xl p-4 bg-red-50 space-y-3">
                        <p className="text-sm text-red-700 font-medium">Подтвердите удаление</p>
                        <p className="text-xs text-red-500">Рынок «{selected.name}» будет удалён навсегда.</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setConfirmDelete(false)}
                            className="flex-1 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors"
                          >
                            Отмена
                          </button>
                          <button
                            disabled={deleting}
                            onClick={handleDelete}
                            className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            {deleting
                              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              : <TrashIcon className="w-4 h-4" />
                            }
                            Удалить
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Footer ── */}
                <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
                  <button
                    onClick={() => setSelected(null)}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors"
                  >
                    Закрыть
                  </button>
                  <button
                    disabled={saving}
                    onClick={handleSave}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                  >
                    {saving
                      ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <CheckCircleIcon className="w-4 h-4" />
                    }
                    Сохранить
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}